import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import DashboardShell from "@/components/layout/DashboardShell";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";
import { Globe, Download, ShieldAlert, Users } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

export default function AdminGeoAccess() {
  const { t } = useT();
  const [settings, setSettings] = useState(null);
  const [leads, setLeads] = useState([]);
  const [blocked, setBlocked] = useState([]);

  useEffect(() => {
    api.get("/admin/geo/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/admin/geo/leads").then((r) => setLeads(r.data || [])).catch(() => {});
    api.get("/admin/geo/blocked").then((r) => setBlocked(r.data || [])).catch(() => {});
  }, []);

  const toggle = async (v) => {
    try {
      const r = await api.patch("/admin/geo/settings", { india_only: v });
      setSettings(r.data);
      toast.success(v ? t("geo.enabledToast") : t("geo.disabledToast"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("msg.saveFailed"));
    }
  };

  const exportCsv = async () => {
    try {
      const r = await api.get("/admin/geo/leads.csv", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "international_leads.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(t("msg.saveFailed"));
    }
  };

  return (
    <DashboardShell title={t("geo.title")} subtitle={t("geo.subtitle")}>
      <div className="am-card p-6 flex items-center gap-4" data-testid="geo-mode-card">
        <span className="w-10 h-10 rounded-2xl bg-accent grid place-items-center shrink-0">
          <Globe className="w-5 h-5 text-primary" />
        </span>
        <div className="flex-1 min-w-0">
          <h2 className="font-display text-lg font-bold">{t("geo.indiaOnly")}</h2>
          <p className="text-xs text-muted-foreground">{t("geo.indiaOnlyHint")}</p>
        </div>
        {settings && (
          <Switch checked={!!settings.india_only} onCheckedChange={toggle} data-testid="geo-india-only-toggle" />
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-6">
        <div className="am-card p-6" data-testid="geo-leads-card">
          <div className="flex items-center gap-3 mb-4">
            <Users className="w-5 h-5 text-primary shrink-0" />
            <h2 className="font-display text-lg font-bold flex-1">{t("geo.leads")} ({leads.length})</h2>
            <Button size="sm" variant="outline" className="rounded-full" onClick={exportCsv} data-testid="geo-export-csv-btn">
              <Download className="w-4 h-4 mr-1.5" /> {t("geo.exportCsv")}
            </Button>
          </div>
          <div className="max-h-[24rem] overflow-y-auto space-y-2">
            {leads.map((l, i) => (
              <div key={`${l.email || l.name}-${l.created_at || i}`} className="rounded-2xl border p-3 text-sm" data-testid={`geo-lead-row-${i}`}>
                <div className="font-semibold">{l.name} <span className="am-chip ml-2 text-[10px]">{l.country}</span></div>
                <div className="text-xs text-muted-foreground">{l.email} · {fmt(l.created_at)}</div>
              </div>
            ))}
            {leads.length === 0 && <p className="py-8 text-center text-sm text-muted-foreground">{t("geo.noLeads")}</p>}
          </div>
        </div>

        <div className="am-card p-6" data-testid="geo-blocked-card">
          <div className="flex items-center gap-3 mb-4">
            <ShieldAlert className="w-5 h-5 text-red-700 shrink-0" />
            <h2 className="font-display text-lg font-bold flex-1">{t("geo.blocked")} ({blocked.length})</h2>
          </div>
          <div className="max-h-[24rem] overflow-y-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground border-b">
                  <th className="pb-2 pr-3">IP</th>
                  <th className="pb-2 pr-3">{t("geo.country")}</th>
                  <th className="pb-2 pr-3">{t("geo.date")}</th>
                  <th className="pb-2">{t("geo.time")}</th>
                </tr>
              </thead>
              <tbody>
                {blocked.map((b, i) => (
                  <tr key={`${b.ip}-${b.created_at || i}`} className="border-b last:border-0">
                    <td className="py-2 pr-3 font-mono text-xs">{b.ip}</td>
                    <td className="py-2 pr-3">{b.country}</td>
                    <td className="py-2 pr-3 text-xs">{b.date}</td>
                    <td className="py-2 text-xs">{b.time}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {blocked.length === 0 && <p className="py-8 text-center text-sm text-muted-foreground">{t("geo.noBlocked")}</p>}
          </div>
        </div>
      </div>
    </DashboardShell>
  );
}
